import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { API_URL } from './config';
import { validateGroupData } from './lib/validateGroupData';
import { RequireAuth } from './components/RequireAuth';
import { LoginView } from './views/LoginView';
import { DashboardView } from './views/DashboardView';
import { AnalysisView } from './views/AnalysisView';
import { GraderView } from './views/GraderView';

function App() {
  const [auth, setAuth] = useState(() => {
    const saved = sessionStorage.getItem('ta_auth');
    return saved ? JSON.parse(saved) : null;
  });
  const [groups, setGroups] = useState([]);
  const [globalToast, setGlobalToast] = useState({ open: false, msg: '', severity: 'success' });

  const handleLogout = useCallback(() => {
    setAuth(null);
    sessionStorage.removeItem('ta_auth');
    setGroups([]);
    alert('You have been logged out.');
  }, []);

  const fetchGroups = useCallback(
    async (token) => {
      try {
        const res = await fetch(`${API_URL}/groups`, {
          headers: { 'x-session-token': token },
        });
        if (res.status === 401) {
          handleLogout();
          return;
        }
        const data = await res.json();
        setGroups(data);
      } catch (err) {
        console.error(err);
      }
    },
    [handleLogout]
  );

  useEffect(() => {
    if (auth) fetchGroups(auth.token);
  }, [auth, fetchGroups]);

  const showGlobalToast = (msg, type) => setGlobalToast({ open: true, msg, severity: type });

  const handleLogin = async (username, password, setError, force = false) => {
    const token = 'Basic ' + btoa(username + ':' + password);
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const data = await res.json();
      if (res.status === 200) {
        const userData = { username, token: data.token };
        setAuth(userData);
        sessionStorage.setItem('ta_auth', JSON.stringify(userData));
        setError('');
      } else if (res.status === 409) {
        if (
          window.confirm(
            data.message ||
              'User is already logged in. Do you want to force log in and kick them out?'
          )
        ) {
          handleLogin(username, password, setError, true);
        } else {
          setError('Login cancelled.');
        }
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection failed. Is server running?');
    }
  };

  const handleCreateGroup = useCallback(
    async (newGroupData, onSuccess) => {
      if (!newGroupData.name || !newGroupData.number || !auth?.token) return;
      try {
        const res = await fetch(`${API_URL}/groups`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': auth.token,
          },
          body: JSON.stringify({
            groupName: newGroupData.name,
            groupNumber: newGroupData.number,
          }),
        });
        if (res.status === 401) {
          handleLogout();
          return;
        }
        const newGroup = await res.json();
        setGroups((prev) => [...prev, newGroup]);
        showGlobalToast(`Group ${newGroup.groupNumber} Created`, 'success');
        if (onSuccess) onSuccess();
      } catch (err) {
        showGlobalToast('Error creating group', 'error');
      }
    },
    [auth, handleLogout]
  );

  const handleSaveGroup = useCallback(
    async (data, silent, onComplete) => {
      if (!auth?.token) return;
      const validationError = validateGroupData(data);
      let toastMsg = 'Changes Saved Successfully';
      let toastType = 'success';
      if (validationError) {
        toastMsg = 'Saved with Warnings';
        toastType = 'warning';
        if (!silent) console.warn('Validation failed:', validationError);
      }
      try {
        const res = await fetch(`${API_URL}/groups/${data._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': auth.token,
          },
          body: JSON.stringify(data),
        });
        if (res.status === 401) {
          handleLogout();
          return;
        }
        if (res.status === 403) {
          if (!silent && onComplete) onComplete('Permission Denied: Not creator.', 'error');
          return;
        }
        if (res.status === 409) {
          if (!silent && onComplete) onComplete('Conflict! Reload required.', 'error');
          alert(
            'CONFLICT DETECTED: Another session has modified this group.\n\nYour changes cannot be saved. The page will reload with the latest data.'
          );
          window.location.reload();
          return;
        }
        const savedData = await res.json();
        if (onComplete) onComplete(toastMsg, toastType, savedData);
        fetchGroups(auth.token);
      } catch (err) {
        if (!silent && onComplete) onComplete('Error saving data', 'error');
      }
    },
    [auth, fetchGroups, handleLogout]
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={auth ? <Navigate to="/" replace /> : <LoginView onLogin={handleLogin} />}
        />
        <Route
          path="/"
          element={
            <RequireAuth auth={auth}>
              <DashboardView
                groups={groups}
                auth={auth}
                onLogout={handleLogout}
                onCreateGroup={handleCreateGroup}
              />
            </RequireAuth>
          }
        />
        <Route
          path="/analysis"
          element={
            <RequireAuth auth={auth}>
              <AnalysisView groups={groups} />
            </RequireAuth>
          }
        />
        <Route
          path="/group/:groupId"
          element={
            <RequireAuth auth={auth}>
              <GraderView auth={auth} onSave={handleSaveGroup} />
            </RequireAuth>
          }
        />
      </Routes>
      <Snackbar
        open={globalToast.open}
        autoHideDuration={6000}
        onClose={() => setGlobalToast((t) => ({ ...t, open: false }))}
      >
        <Alert
          onClose={() => setGlobalToast((t) => ({ ...t, open: false }))}
          severity={globalToast.severity}
          sx={{ width: '100%' }}
        >
          {globalToast.msg}
        </Alert>
      </Snackbar>
    </BrowserRouter>
  );
}

export default App;
